import React, { useEffect, useState, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import HarvestingTopNav from '../components/HarvestingTopNav';
import './MyHarvestingPage.css';

// Fix default Leaflet icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const getNumberedIcon = (n, dataType) => {
  const color = dataType === 'berry-harvesting' ? '#7b1fa2' : '#2e7d32';
  return L.divIcon({
    className: 'my-harvest-marker',
    html: `<div style="
      background:${color};color:white;width:30px;height:30px;
      border-radius:50%;border:2px solid white;
      box-shadow:0 2px 6px rgba(0,0,0,0.4);
      display:flex;align-items:center;justify-content:center;
      font-size:13px;font-weight:700;line-height:1;">${n}</div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    popupAnchor: [0, -17],
  });
};

const newPinIcon = L.divIcon({
  className: 'new-pin-marker',
  html: `<div style="
    background:#f44336;color:white;width:30px;height:30px;
    border-radius:50%;border:2px solid white;
    box-shadow:0 2px 6px rgba(0,0,0,0.4);
    display:flex;align-items:center;justify-content:center;font-size:16px;">📍</div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 17],
  popupAnchor: [0, -17],
});

const MapClickHandler = ({ active, onMapClick }) => {
  useMapEvents({
    click(e) {
      if (active) onMapClick([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
};

const EMPTY_LOG = { type: 'harvesting', observations: '', abundance: '', season: '' };

const MyHarvestingPage = () => {
  const { currentUser } = useContext(AuthContext);
  const userId = currentUser?._id || currentUser?.id;

  const [mySubmissions, setMySubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [viewMode, setViewMode] = useState('new');
  const [showAllExisting, setShowAllExisting] = useState(false);
  const [actionMsg, setActionMsg] = useState('');

  // Log-new form
  const [logForm, setLogForm] = useState(EMPTY_LOG);
  const [newLocation, setNewLocation] = useState(null);
  const [pickingNew, setPickingNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [logMsg, setLogMsg] = useState('');

  // Expanded edit
  const [expandedId, setExpandedId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLocation, setEditLocation] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const defaultCenter = [48.3809, -89.2477];

  const fetchMySubmissions = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setFetchError('');
    try {
      const [genRes, berryRes] = await Promise.allSettled([
        api.get('/api/data/harvesting'),
        api.get('/api/data/berry-harvesting'),
      ]);
      const combined = [];
      const addItems = (res, dataType) => {
        if (res.status !== 'fulfilled') return;
        const items = res.value?.data?.items || res.value?.data || [];
        items.forEach((item) => {
          const ownerId =
            (typeof item.createdBy === 'object' ? item.createdBy?._id : item.createdBy) || '';
          if (String(ownerId) === String(userId)) {
            combined.push({ ...item, _dataType: dataType });
          }
        });
      };
      addItems(genRes, 'harvesting');
      addItems(berryRes, 'berry-harvesting');
      combined.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setMySubmissions(combined);
    } catch {
      setFetchError('Could not load your harvest submissions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchMySubmissions(); }, [fetchMySubmissions]);

  // Log new submit
  const handleLogSubmit = async (e) => {
    e.preventDefault();
    if (!newLocation) { setLogMsg('⚠️ Please pick a location on the map first.'); return; }
    setSubmitting(true);
    setLogMsg('');
    try {
      await api.post(`/api/data/${logForm.type}`, {
        freeTextData: { observations: logForm.observations },
        structuredData: { abundance: logForm.abundance, harvestSeason: logForm.season },
        location: newLocation,
        isPublic: false,
      });
      setLogMsg('✓ Activity logged!');
      setActionMsg('New activity added.');
      setLogForm(EMPTY_LOG);
      setNewLocation(null);
      setPickingNew(false);
      setTimeout(() => setLogMsg(''), 1200);
      fetchMySubmissions();
    } catch {
      setLogMsg('❌ Failed to save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Start editing an item
  const startEdit = (item) => {
    setExpandedId(item._id);
    setEditForm({
      observations: item.freeTextData?.observations || '',
      abundance: item.structuredData?.abundance || '',
      season: item.structuredData?.harvestSeason || '',
    });
    setEditLocation(item.location?.length === 2 ? [item.location[0], item.location[1]] : null);
  };

  const handleEditSave = async (item) => {
    setSavingId(item._id);
    try {
      await api.put(`/api/data/${item._dataType}/${item._id}`, {
        freeTextData: { observations: editForm.observations },
        structuredData: { abundance: editForm.abundance, harvestSeason: editForm.season },
        ...(editLocation ? { location: editLocation } : {}),
      });
      setExpandedId(null);
      setActionMsg('Entry updated.');
      fetchMySubmissions();
    } catch {
      alert('Failed to save changes. Please try again.');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (item) => {
    const ok = window.confirm('Delete this harvest entry? This cannot be undone.');
    if (!ok) return;

    setDeletingId(item._id);
    try {
      await api.delete(`/api/data/${item._dataType}/${item._id}`);
      if (expandedId === item._id) {
        setExpandedId(null);
      }
      setActionMsg('Entry deleted.');
      fetchMySubmissions();
    } catch {
      alert('Failed to delete entry. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleMapClick = (latlng) => {
    if (pickingNew) { setNewLocation(latlng); setPickingNew(false); }
  };

  const activePickingLocation = pickingNew;
  const displayItems = showAllExisting ? mySubmissions : mySubmissions.slice(0, 10);

  return (
    <div className="my-harvest-page">
      <HarvestingTopNav />

      {/* Header */}
      <div className="mhp-header">
        <div className="mhp-header-left">
          <h1>My Harvest Activity</h1>
          <p>Choose a tile: add a new point or manage your existing entries</p>
        </div>
        <div className="mhp-header-actions">
          <Link to="/harvesting" className="mhp-btn-back">← Portal</Link>
        </div>
      </div>

      <div className="mhp-mode-tiles">
        <button
          className={`mhp-mode-tile ${viewMode === 'new' ? 'active' : ''}`}
          onClick={() => {
            setViewMode('new');
            setExpandedId(null);
          }}
        >
          <span className="mhp-mode-icon">➕</span>
          <span className="mhp-mode-text">
            <strong>Add New Point</strong>
            <small>Map + add form</small>
          </span>
        </button>
        <button
          className={`mhp-mode-tile ${viewMode === 'existing' ? 'active' : ''}`}
          onClick={() => {
            if (viewMode === 'existing' && !showAllExisting) {
              setShowAllExisting(true);
            }
            setViewMode('existing');
            setPickingNew(false);
          }}
        >
          <span className="mhp-mode-icon">📋</span>
          <span className="mhp-mode-text">
            <strong>Existing Entries</strong>
            <small>{showAllExisting ? `Showing all (${mySubmissions.length})` : 'Showing last 10'}</small>
          </span>
        </button>
      </div>

      {actionMsg && <div className="mhp-action-msg">{actionMsg}</div>}

      {/* Map click banner */}
      {viewMode === 'new' && activePickingLocation && (
        <div className="mhp-pick-banner">
          📍 Click anywhere on the map to set the location
        </div>
      )}

      {viewMode === 'new' && (
        <div className="mhp-body">
          <div className="mhp-map-panel" style={{ height: '100%', width: '100%' }}>
            <MapContainer
              center={defaultCenter}
              zoom={10}
              scrollWheelZoom={true}
              className="leaflet-container"
              style={{ cursor: activePickingLocation ? 'crosshair' : undefined }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapClickHandler active={activePickingLocation} onMapClick={handleMapClick} />

              {mySubmissions.slice(0, 10).map((item, idx) =>
                item.location?.length === 2 ? (
                  <Marker
                    key={item._id}
                    position={[item.location[0], item.location[1]]}
                    icon={getNumberedIcon(idx + 1, item._dataType)}
                  >
                    <Popup>
                      <div className="mhp-popup">
                        <div className="mhp-popup-type">
                          {item._dataType === 'berry-harvesting' ? '🫐 Berry Harvest' : '🌿 General Harvest'}
                        </div>
                        <strong>#{idx + 1}</strong> — {new Date(item.createdAt).toLocaleDateString('en-CA')}
                        {item.freeTextData?.observations && <p>{item.freeTextData.observations}</p>}
                      </div>
                    </Popup>
                  </Marker>
                ) : null
              )}

              {newLocation && (
                <Marker position={newLocation} icon={newPinIcon}>
                  <Popup>New location selected</Popup>
                </Marker>
              )}
            </MapContainer>
          </div>

          <div className="mhp-sidebar">
            <div className="mhp-log-form">
              <h3>Add New Point</h3>
              <form onSubmit={handleLogSubmit}>
                <div className="mhp-field">
                  <label>Type</label>
                  <select value={logForm.type} onChange={e => setLogForm(f => ({ ...f, type: e.target.value }))}>
                    <option value="harvesting">🌿 General Harvest</option>
                    <option value="berry-harvesting">🫐 Berry Harvest</option>
                  </select>
                </div>
                <div className="mhp-field">
                  <label>Observations</label>
                  <textarea
                    rows={3}
                    placeholder="What did you find? Any notes about conditions?"
                    value={logForm.observations}
                    onChange={e => setLogForm(f => ({ ...f, observations: e.target.value }))}
                  />
                </div>
                <div className="mhp-field-row">
                  <div className="mhp-field">
                    <label>Abundance</label>
                    <select value={logForm.abundance} onChange={e => setLogForm(f => ({ ...f, abundance: e.target.value }))}>
                      <option value="">—</option>
                      <option>Low</option>
                      <option>Medium</option>
                      <option>High</option>
                      <option>Abundant</option>
                    </select>
                  </div>
                  <div className="mhp-field">
                    <label>Season</label>
                    <select value={logForm.season} onChange={e => setLogForm(f => ({ ...f, season: e.target.value }))}>
                      <option value="">—</option>
                      <option>Spring</option>
                      <option>Summer</option>
                      <option>Fall</option>
                      <option>Winter</option>
                    </select>
                  </div>
                </div>
                <div className="mhp-field">
                  <label>Location</label>
                  <div className="mhp-loc-row">
                    <button
                      type="button"
                      className={`mhp-btn-pick ${pickingNew ? 'active' : ''}`}
                      onClick={() => setPickingNew(v => !v)}
                    >
                      {pickingNew ? '✕ Cancel pick' : '📍 Pick on map'}
                    </button>
                    {newLocation ? (
                      <span className="mhp-loc-value">
                        {newLocation[0].toFixed(4)}, {newLocation[1].toFixed(4)}
                        <button type="button" className="mhp-loc-clear" onClick={() => setNewLocation(null)}>✕</button>
                      </span>
                    ) : (
                      <span className="mhp-loc-hint">No location set</span>
                    )}
                  </div>
                </div>
                {logMsg && <div className={logMsg.startsWith('✓') ? 'mhp-form-success' : 'mhp-form-error'}>{logMsg}</div>}
                <button type="submit" className="mhp-btn-submit" disabled={submitting}>
                  {submitting ? 'Saving…' : '✓ Add Point'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {viewMode === 'existing' && (
        <div className="mhp-body existing-mode">
          <div className="mhp-existing-panel">
            <div className="mhp-list-header">
              <span className="mhp-list-title">Existing Entries</span>
              <div className="mhp-list-header-actions">
                {mySubmissions.length > 10 && !showAllExisting && (
                  <button className="mhp-expand-btn" onClick={() => setShowAllExisting(true)}>
                    Show All Entries
                  </button>
                )}
                {showAllExisting && mySubmissions.length > 10 && (
                  <button className="mhp-expand-btn" onClick={() => setShowAllExisting(false)}>
                    Show Last 10
                  </button>
                )}
                <span className="mhp-list-count">{showAllExisting ? `${mySubmissions.length} shown` : `${Math.min(10, mySubmissions.length)} of ${mySubmissions.length}`}</span>
              </div>
            </div>

            {loading && <div className="mhp-loading">Loading…</div>}
            {fetchError && <div className="mhp-fetch-error">{fetchError}</div>}

            {!loading && mySubmissions.length === 0 && !fetchError && (
              <div className="mhp-empty">
                <p>No harvest activity logged yet.</p>
              </div>
            )}

            <div className="mhp-list">
              {displayItems.map((item, idx) => {
                const isOpen = expandedId === item._id;
                return (
                  <div key={item._id} className={`mhp-card ${isOpen ? 'open' : ''} card-${item.status}`}>
                    <button
                      className="mhp-card-head"
                      onClick={() => {
                        if (isOpen) { setExpandedId(null); }
                        else { startEdit(item); }
                      }}
                    >
                      <div className="mhp-card-left">
                        <span className="mhp-card-num">#{idx + 1}</span>
                        <span className="mhp-card-icon">
                          {item._dataType === 'berry-harvesting' ? '🫐' : '🌿'}
                        </span>
                        <div className="mhp-card-meta">
                          <span className="mhp-card-date">
                            {new Date(item.createdAt).toLocaleDateString('en-CA')}
                          </span>
                          {item.freeTextData?.observations && (
                            <span className="mhp-card-preview">
                              {item.freeTextData.observations.length > 45
                                ? item.freeTextData.observations.slice(0, 45) + '…'
                                : item.freeTextData.observations}
                            </span>
                          )}
                          {item.location?.length === 2 && (
                            <span className="mhp-card-loc">
                              📍 {item.location[0].toFixed(3)}, {item.location[1].toFixed(3)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mhp-card-right">
                        <span className={`mhp-status-chip mhp-chip-${item.status}`}>{item.status}</span>
                        <span className="mhp-chevron">{isOpen ? '▲' : '▼'}</span>
                      </div>
                    </button>

                    {isOpen && (
                      <div className="mhp-card-body">
                        <div className="mhp-field">
                          <label>Observations</label>
                          <textarea
                            rows={3}
                            value={editForm.observations}
                            onChange={e => setEditForm(f => ({ ...f, observations: e.target.value }))}
                          />
                        </div>
                        <div className="mhp-field-row">
                          <div className="mhp-field">
                            <label>Abundance</label>
                            <select
                              value={editForm.abundance}
                              onChange={e => setEditForm(f => ({ ...f, abundance: e.target.value }))}
                            >
                              <option value="">—</option>
                              <option>Low</option>
                              <option>Medium</option>
                              <option>High</option>
                              <option>Abundant</option>
                            </select>
                          </div>
                          <div className="mhp-field">
                            <label>Season</label>
                            <select
                              value={editForm.season}
                              onChange={e => setEditForm(f => ({ ...f, season: e.target.value }))}
                            >
                              <option value="">—</option>
                              <option>Spring</option>
                              <option>Summer</option>
                              <option>Fall</option>
                              <option>Winter</option>
                            </select>
                          </div>
                        </div>
                        <div className="mhp-field-row">
                          <div className="mhp-field">
                            <label>Latitude</label>
                            <input
                              type="text"
                              value={editLocation?.[0] ?? ''}
                              onChange={(e) => {
                                const lat = parseFloat(e.target.value);
                                setEditLocation((prev) => [Number.isFinite(lat) ? lat : '', prev?.[1] ?? '']);
                              }}
                            />
                          </div>
                          <div className="mhp-field">
                            <label>Longitude</label>
                            <input
                              type="text"
                              value={editLocation?.[1] ?? ''}
                              onChange={(e) => {
                                const lng = parseFloat(e.target.value);
                                setEditLocation((prev) => [prev?.[0] ?? '', Number.isFinite(lng) ? lng : '']);
                              }}
                            />
                          </div>
                        </div>
                        <div className="mhp-card-actions">
                          <button
                            className="mhp-btn-save"
                            disabled={savingId === item._id}
                            onClick={() => {
                              const validLocation =
                                Array.isArray(editLocation)
                                && Number.isFinite(editLocation[0])
                                && Number.isFinite(editLocation[1]);
                              if (!validLocation) {
                                alert('Please provide valid latitude and longitude.');
                                return;
                              }
                              handleEditSave(item);
                            }}
                          >
                            {savingId === item._id ? 'Saving…' : '✓ Save Changes'}
                          </button>
                          <button
                            className="mhp-btn-delete"
                            disabled={deletingId === item._id}
                            onClick={() => handleDelete(item)}
                          >
                            {deletingId === item._id ? 'Deleting…' : 'Delete'}
                          </button>
                          <button
                            className="mhp-btn-cancel"
                            onClick={() => { setExpandedId(null); }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyHarvestingPage;
